import type { Restaurant } from '@shared/types/restaurant';
import { api, ApiError } from './apiClient';

// Re-export ApiError for backward compatibility
export { ApiError };

/**
 * Fetches restaurant data by subdomain prefix.
 *
 * @param subdomainPrefix - The subdomain prefix (e.g., "pepito", "test")
 * @param apiBaseUrl - The API base URL to use
 * @returns Restaurant data from the API
 * @throws ApiError if request fails or restaurant not found
 */
export async function getRestaurantBySubdomain(
  subdomainPrefix: string,
  apiBaseUrl: string
): Promise<Restaurant> {
  try {
    const restaurant = await api.get<Restaurant>(
      '/api/get-restaurant-info-subdomain',
      {
        baseURL: apiBaseUrl,
        params: { subdomain: subdomainPrefix },
        skipAuth: true, // Public endpoint, no auth needed
      }
    );

    // Validate we got valid data
    if (!restaurant || !restaurant.id) {
      throw new ApiError('Invalid response from API', undefined, 'INVALID_RESPONSE');
    }

    return restaurant;
  } catch (error) {
    // Re-throw ApiError instances with specific codes for restaurant errors
    if (error instanceof ApiError) {
      if (error.status === 404) {
        throw new ApiError('Restaurant not found', 404, 'RESTAURANT_NOT_FOUND');
      }
      throw error;
    }

    // Unknown error
    throw new ApiError(
      error instanceof Error ? error.message : 'Unknown error occurred',
      undefined,
      'NETWORK_ERROR'
    );
  }
}
